import { Box, Card, CardContent, Drawer, Typography } from '@mui/material';
import MessageIcon from 'components/common/MessageIcon';
import { bool, object, string } from 'prop-types';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import WhatsAppTextFormatter from 'components/@home/@messages/ChatPanel/MessagesList/MessageGroup/WhatsAppTextFormatter';
import Channel from 'services/api/Channel';
import messageShape from 'shapes/message';
import { getMessageType, MESSAGE_TYPES } from 'utils/getMessageType';
import getMoment from 'utils/getMoment';
import CloseButton from '../CloseButton';
import connector from './connector';
import ContentProgressBars from './ContentProgressBars';
import MemberList from './MemberList';

const getContentMode = message => {
  if (!message) return null;
  const type = getMessageType(message);
  if (type === MESSAGE_TYPES.ARTICLE) return 'article';
  if (type === MESSAGE_TYPES.VIDEO) return 'video';
  return 'basic';
};

const EmployeeListDrawer = ({
  actions,
  channel = null,
  isOpen,
  isCurrentChannel,
  message = null,
  title,
}) => {
  const handleClose = useCallback(() => {
    actions.drawers.employeeList.close();
  }, [actions.drawers.employeeList]);

  const contentMode = useMemo(() => getContentMode(message), [message]);
  const hasContentStats = contentMode === 'video' || contentMode === 'article';

  const membersRef = useRef({});
  const [membersRevision, setMembersRevision] = React.useState(0);
  const [counters, setCounters] = React.useState({});
  useEffect(() => {
    if (isOpen) {
      setCounters({});
      membersRef.current = {};
      setMembersRevision(0);
    }
  }, [channel?._id, message?._id, isOpen]);
  const loadMoreItems = useCallback(
    async (startIndex, stopIndex) => {
      if (!channel?._id) {
        return;
      }
      const res = await Channel.getMembers(channel._id, {
        message: message?._id,
        skip: startIndex,
        limit: stopIndex - startIndex + 1,
        ...(hasContentStats && { contentStats: 1 }),
      });
      res.members.forEach((member, index) => {
        membersRef.current[startIndex + index] = member;
      });
      setMembersRevision(v => v + 1);
      setCounters({
        read: res.read,
        totalCount: res.totalCount,
        active: res.active,
        videoWatches: res.videoWatches,
        articleReads: res.articleReads,
        groups: res.groups,
      });
    },
    [channel?._id, hasContentStats, message?._id],
  );

  if (!channel || !isCurrentChannel) {
    return null;
  }

  const messageTitle =
    message?.text?.replace('attachments', '').trim() ||
    message?.title ||
    message?.article?.title ||
    '';

  return (
    <Drawer anchor="right" open={isOpen} onClose={handleClose}>
      <Box sx={{ display: 'flex', flexGrow: 1 }}>
        <Box sx={{ paddingTop: '20px', paddingLeft: '20px' }}>
          <CloseButton onClick={handleClose} />
        </Box>
        <Box
          sx={{
            paddingTop: '20px',
            paddingLeft: '30px',
            paddingRight: '30px',
            minWidth: 500,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '20px',
            }}
          >
            <Typography variant="h5">{title}</Typography>
          </Box>
          {message && (
            <>
              <Box
                sx={theme => ({
                  display: 'flex',
                  gap: '12px',
                  padding: '16px',
                  marginBottom: '16px',
                  backgroundColor: theme.palette.action.hover,
                  borderRadius: '8px',
                })}
              >
                <Box
                  sx={theme => ({
                    width: 40,
                    height: 40,
                    borderRadius: '8px',
                    backgroundColor: theme.palette.primary.main,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: theme.palette.primary.contrastText,
                    flexShrink: 0,
                    overflow: 'hidden',
                  })}
                >
                  <MessageIcon message={message} textIcon coverImages />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    variant="body2"
                    noWrap
                    sx={{
                      fontWeight: 600,
                      maxWidth: 350,
                    }}
                  >
                    <WhatsAppTextFormatter inline>{messageTitle}</WhatsAppTextFormatter>
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.secondary',
                    }}
                  >
                    {getMoment(message.createdAt).format('LLL')}
                  </Typography>
                </Box>
              </Box>
              <Card variant="outlined" sx={{ mb: 2 }}>
                <CardContent>
                  <ContentProgressBars contentMode={contentMode} counters={counters} />
                </CardContent>
              </Card>
            </>
          )}
          <MemberList
            totalCount={counters.totalCount}
            membersRef={membersRef}
            membersRevision={membersRevision}
            loadMoreItems={loadMoreItems}
            showReadStatus={!!message}
            contentMode={contentMode}
            groupCounts={counters.groups}
          />
        </Box>
      </Box>
    </Drawer>
  );
};

EmployeeListDrawer.propTypes = {
  title: string.isRequired,
  channel: object,
  actions: object.isRequired,
  isOpen: bool.isRequired,
  message: messageShape,
  isCurrentChannel: bool.isRequired,
};

export default connector(EmployeeListDrawer);
